import { useEffect, useState } from 'react';
import { createTeleporter, Teleporter } from 'react-teleporter';
import { action, actionSet, createStoreEventBus } from '@twocatmoon/react-actions';



// #region Schema & State

export interface ModalState {
  modal: {
    name: string
    options?: {
      hideCloseButton?: boolean
      onClose?: (data: ModalState['modal']['data']) => Promise<void>
      onEnter?: (data: ModalState['modal']['data']) => Promise<ModalState['modal']['data']>
    }
    data?: unknown
  } | null
  transition: {
    name: string
    timeout: number
  }
}

const initialState = {
  modal: null,
  transition: {
    name: '',
    timeout: 0
  }
};

// #endregion



// #region Actions

const modalActions = {
  setModal: action<ModalState, ModalState['modal']>((prevState, modal) => {
    return {
      ...prevState,
      modal
    };
  }),

  clearModal: action<ModalState, undefined>((prevState) => {
    return {
      ...prevState,
      modal: null
    };
  }),

  setTransition: action<ModalState, ModalState['transition']>((prevState, transition) => {
    return {
      ...prevState,
      transition
    };
  }),
};

// #endregion



// #region Action Sets

const modalActionSets = {
  openModal: actionSet<ModalState, ModalState['modal']>(async (dispatch, state, modal) => {
    if (state.modal?.options?.onClose) await state.modal.options.onClose(state.modal);
    if (modal.options?.onEnter) modal.data = await modal.options.onEnter(modal);

    dispatch(modalActions.setModal(modal));
  }),

  closeModal: actionSet<ModalState, ModalState['modal']>(async (dispatch, state) => {
    if (state.modal?.options?.onClose) await state.modal.options.onClose(state.modal);

    dispatch(modalActions.clearModal());
  }),
};

// #endregion



// #region Exports

let teleporter;

const { useStore } = createStoreEventBus<ModalState>(initialState, modalActions, { ssr: true });

export function useModal () {
  if (teleporter === undefined && typeof window !== 'undefined') {
    teleporter = createTeleporter({ multiSources: true });
  }

  const [ state, dispatch, execute ] = useStore();
  const [ currentModal, setCurrentModal ] = useState(state.modal);

  useEffect(() => {
    setCurrentModal(state.modal);
  }, [ state ]);

  function openModal (name: ModalState['modal']['name'], options?: ModalState['modal']['options'], data?: unknown) {
    execute(modalActionSets.openModal({
      name,
      options,
      data,
    }));
  }

  function closeModal () {
    execute(modalActionSets.closeModal());
  }

  function setTransition (transition: ModalState['transition']) {
    dispatch(modalActions.setTransition(transition));
  }

  return { 
    state, 
    currentModal, 
    openModal, 
    closeModal, 
    setTransition,
    Teleporter: teleporter,
  };
}

// #endregion
